import React, { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { SearchField } from '@sas/nova-commons/components/SearchField';
import { PanelListItem, panelItemHeight } from './PanelListItem';
import { List } from '@sas/nova-commons/components/List';
import { renderInlineCssVars, useDesignTokens } from '@sas/nova-commons/theme';
import { Popover } from '@sas/nova-commons/components/Popover';
import { useResizeObserverRef } from '@sas/nova-commons/hooks/useResizeObserver';
import { LocationPopover } from './LocationPopover';
import { useI18n } from '../../../../locale/I18nContext';
import { useLatestRef } from '../../../hooks';
import { Label } from '@sas/nova-commons/components/Label';
import { withLabels } from '@sas/nova-commons/components/withLabels';
import useOnFocusOut from '@sas/nova-commons/hooks/useOnFocusOut';
import { connect } from 'react-redux';
import { selectObjectSearchState, selectVisualizationState } from '../../../../store/selectors';
import './PanelSearchField.css';
import cn from './PanelSearchField.css.json';
const SearchFieldWithLabels = withLabels(SearchField);
export const PanelSearchFieldBase = forwardRef((props, ref) => {
    var _a, _b, _c, _d;
    const localize = useI18n();
    const dt = useDesignTokens();
    const tokens = renderInlineCssVars({
        bg: dt.backgrounds.panes.bg,
        bd: dt.backgroundsDepth.standard.bd,
    }, 'panelSearchField');
    const inputRef = useRef(null);
    useImperativeHandle(ref, () => ({
        focus: () => { var _a; return (_a = inputRef.current) === null || _a === void 0 ? void 0 : _a.focus(); },
    }), []);
    const [hasFocus, setHasFocus] = useState(false);
    const [inputHasFocus, setInputHasFocus] = useState(false);
    const searchFieldFocusHandlers = useOnFocusOut(() => {
        var _a;
        setHasFocus(false);
        if (!props.contentId ||
            ((_a = document.getElementById(props.contentId)) === null || _a === void 0 ? void 0 : _a.contains(document.activeElement))) {
            return;
        }
        // Disable pin mode when something else gets focus
        props.onFocusOut();
    });
    const [searchFieldWidth, setSearchFieldWidth] = useState(undefined);
    const [setSearchFieldRef] = useResizeObserverRef((entry) => {
        setSearchFieldWidth(entry.target.getBoundingClientRect().width);
    });
    const [value, setValue] = useState(undefined);
    const hasLocation = !!((_a = props.feature) === null || _a === void 0 ? void 0 : _a.location);
    {
        const onValueAddedRef = useLatestRef(props.onValueAdded);
        useEffect(() => {
            if (hasLocation) {
                setValue(undefined);
                onValueAddedRef.current();
            }
        }, [hasLocation, onValueAddedRef]);
        const refOnFocusIn = useLatestRef(props.onEmptyFieldFocused);
        useEffect(() => {
            if (hasFocus && !hasLocation) {
                refOnFocusIn.current();
            }
        }, [hasFocus, hasLocation, refOnFocusIn]);
    }
    {
        // Clear hints when a candidate is added
        const hasCandidate = !!props.locationCandidate;
        const refOnUpdateHints = useLatestRef(props.onUpdateHints);
        useEffect(() => {
            if (hasCandidate) {
                refOnUpdateHints.current({ searchTerm: '' });
            }
        }, [refOnUpdateHints, hasCandidate]);
    }
    const pins = (_b = props.features) === null || _b === void 0 ? void 0 : _b.filter((feature) => feature.type === 'pin');
    const results = (props.panelType === 'route' || props.panelType === 'area') && value
        ? [
            ...props.searchHints,
            ...((pins === null || pins === void 0 ? void 0 : pins.filter((pin) => {
                var _a;
                return (pin.name.toLocaleLowerCase().includes(value.toLocaleLowerCase()) ||
                    ((_a = pin.location) === null || _a === void 0 ? void 0 : _a.toLocaleLowerCase().includes(value.toLocaleLowerCase())));
            })) || []),
        ]
        : props.searchHints;
    const [popoverIsOpen, setPopoverIsOpen] = useState(false);
    useEffect(() => {
        setPopoverIsOpen(value !== undefined && !props.locationCandidate);
    }, [props.locationCandidate, value]);
    return (React.createElement("div", Object.assign({ ref: (e) => {
            var _a;
            setSearchFieldRef((_a = e === null || e === void 0 ? void 0 : e.lastElementChild) !== null && _a !== void 0 ? _a : null);
        }, style: tokens, onFocus: (e) => {
            setHasFocus(true);
            searchFieldFocusHandlers.onFocus();
            if (e.target === inputRef.current) {
                setInputHasFocus(true);
            }
        }, onBlur: (e) => {
            if (e.target === inputRef.current) {
                setInputHasFocus(false);
            }
            searchFieldFocusHandlers.onBlur();
        } }, (!hasLocation && { 'data-autofocus-inside': true })),
        props.label && (
        // SearchField doesn't work with ariaLabelledBy, so we have to render the label manually
        React.createElement(Label, null, props.label)),
        React.createElement(SearchFieldWithLabels, { ariaLabel: (_c = props.ariaLabel) !== null && _c !== void 0 ? _c : props.label, fieldWidth: "100%", className: cn.panelSearchField, onChange: (e) => {
                setValue(e.value);
                props.onInvalidateLocation();
                props.onUpdateHints({ searchTerm: e.value });
            }, onCommit: (e) => {
                if (e.value) {
                    // Automatically selects the first search hint when the user commits
                    const firstHint = results[0];
                    props.onApplyHint({
                        searchTerm: e.value,
                        hint: firstHint === null || firstHint === void 0 ? void 0 : firstHint.name,
                    });
                }
                else {
                    setValue(undefined);
                }
            }, value: (_d = value !== null && value !== void 0 ? value : props.feature.location) !== null && _d !== void 0 ? _d : '', placeholder: localize({
                key: inputHasFocus && !props.locationCandidate
                    ? 'PinPanel.searchClickPlaceholder.txt'
                    : 'PinPanel.searchPlaceholder.txt',
            }), inputRef: inputRef }),
        React.createElement(Popover, { isOpen: popoverIsOpen, referenceElementRef: inputRef, hideArrow: true, placement: 'bottom-start', isModal: false, focusLockProps: { disabled: inputHasFocus }, onClose: () => {
                setPopoverIsOpen(false);
            }, style: { width: searchFieldWidth, padding: 0, cursor: 'auto' } },
            React.createElement(List, { selectionMode: "single", rowCount: results.length, showSeparator: () => false, expandToFillWidth: true, itemHeight: () => 'double', height: panelItemHeight * (results.length || 2), renderItem: ({ index }) => {
                    const item = results[index];
                    if (item) {
                        return (React.createElement(PanelListItem, { searchResultMode: true, item: item, panelType: props.panelType }));
                    }
                    else {
                        return null;
                    }
                }, onSelect: (row) => {
                    var _a, _b;
                    const selectedValue = results[parseInt(row === null || row === void 0 ? void 0 : row.values().next().value)];
                    if (selectedValue.type === 'hint') {
                        props.onApplyHint({
                            hint: selectedValue.name,
                        });
                    }
                    else if (selectedValue.type === 'pin') {
                        // TODO: when linking is enabled, this will need to handle applying pin location OR linking to pin
                        (_a = props.onApplyPin) === null || _a === void 0 ? void 0 : _a.call(props, {
                            pin: selectedValue,
                        });
                    }
                    setValue(undefined);
                    (_b = inputRef.current) === null || _b === void 0 ? void 0 : _b.focus();
                } })),
        React.createElement(LocationPopover, { id: props.id, contentId: props.contentId, title: props.locationPopoverTitle, locationCandidate: props.locationCandidate, onOutsideClick: () => {
                searchFieldFocusHandlers.onBlur();
                props.onOutsideClick();
            }, onConfirm: () => {
                setHasFocus(false);
                props.onConfirm();
            }, onCancel: () => {
                setHasFocus(false);
                props.onCancel();
            } })));
});
export const PanelSearchField = connect((state, props) => {
    var _a, _b;
    const { contentId } = (_a = selectVisualizationState(state, props.id)) !== null && _a !== void 0 ? _a : {};
    const { features } = (_b = selectObjectSearchState(state, props.id)) !== null && _b !== void 0 ? _b : {};
    return { contentId, features };
}, undefined, undefined, { forwardRef: true })(PanelSearchFieldBase);
